import { useEffect, useLayoutEffect, useRef, type CSSProperties } from 'react';
import { Terminal, type ITerminalOptions, type ITheme } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import { WebLinksAddon } from '@xterm/addon-web-links';
import { CanvasAddon } from '@xterm/addon-canvas';
import '@xterm/xterm/css/xterm.css';
import { cn } from '@/lib/cn';
import { terminalTheme } from '@/lib/terminalTheme';

/* ZTA design evaluation F40 — the one xterm setup.
 *
 * Every terminal in the console (SSH and kubectl full-page routes, the SSH
 * dialog, the live-session join page and the session replay) mounts its
 * screen through this component: one place for the theme (lib/terminalTheme),
 * the font, fit-to-container, resize and dispose. Connection logic stays with
 * the caller: it gets the Terminal in onReady, sends keys from onData and
 * tells the server the PTY size from onResize.
 *
 * The chrome around the screen (title, status, actions) is TerminalChrome. */

/** The app's mono face first (loaded in main.tsx), then common fallbacks. */
export const TERMINAL_FONT_FAMILY =
    "'Geist Mono Variable', 'Geist Mono', 'JetBrains Mono', 'Fira Code', Menlo, Monaco, 'Courier New', monospace";

export interface TerminalSize { cols: number; rows: number; }

export interface TerminalSurfaceProps {
    /** Canvas palette. Default: the live-terminal palette. */
    theme?: ITheme;
    /** Read-only screen (replay, observer): no cursor blink, no stdin. */
    readOnly?: boolean;
    fontSize?: number;
    scrollback?: number;
    /** Clickable URLs in the output. Default true for live terminals. */
    webLinks?: boolean;
    /** Use the canvas renderer (faster for heavy output). Falls back silently. */
    canvasRenderer?: boolean;
    /** Extra xterm options, applied last. */
    options?: ITerminalOptions;
    /** Called once per terminal instance, after it is opened and first fitted. */
    onReady?: (term: Terminal) => void;
    /** Called just before the terminal is disposed (unmount). */
    onDispose?: () => void;
    /** Every sequence the terminal produces: keys, Enter, arrows, paste… */
    onData?: (data: string) => void;
    /** The fitted size changed (container resize, window resize, fullscreen). */
    onResize?: (size: TerminalSize) => void;
    /** Focus the terminal once it is ready. */
    autoFocus?: boolean;
    /** Accessible name of the screen region. */
    'aria-label'?: string;
    className?: string;
    style?: CSSProperties;
}

export default function TerminalSurface({
    theme = terminalTheme,
    readOnly = false,
    fontSize = 14,
    scrollback = 10000,
    webLinks = !readOnly,
    canvasRenderer = false,
    options,
    onReady,
    onDispose,
    onData,
    onResize,
    autoFocus = false,
    'aria-label': ariaLabel = 'Terminal',
    className,
    style,
}: TerminalSurfaceProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const termRef = useRef<Terminal | null>(null);
    // Latest callbacks, so a new closure from the caller never re-creates the terminal.
    const cb = useRef({ onReady, onDispose, onData, onResize });
    useLayoutEffect(() => {
        cb.current = { onReady, onDispose, onData, onResize };
    });

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const term = new Terminal({
            cursorBlink: !readOnly,
            cursorStyle: 'block',
            disableStdin: readOnly,
            fontSize,
            fontFamily: TERMINAL_FONT_FAMILY,
            theme,
            scrollback,
            convertEol: true,
            allowProposedApi: false,
            ...options,
        });
        const fit = new FitAddon();
        term.loadAddon(fit);
        if (webLinks) term.loadAddon(new WebLinksAddon());
        if (canvasRenderer) {
            try { term.loadAddon(new CanvasAddon()); } catch { /* DOM renderer is fine */ }
        }
        term.open(el);
        termRef.current = term;

        let last: TerminalSize = { cols: term.cols, rows: term.rows };
        const refit = () => {
            if (termRef.current !== term) return;
            try { fit.fit(); } catch { /* not laid out yet */ }
            if (term.cols !== last.cols || term.rows !== last.rows) {
                last = { cols: term.cols, rows: term.rows };
                cb.current.onResize?.(last);
            }
        };
        refit();

        const dataSub = term.onData((d) => cb.current.onData?.(d));

        // Fit to the container, not the window: covers dialogs, fullscreen and
        // side panels as well as window resizes.
        let frame = 0;
        const schedule = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(refit); };
        const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(schedule) : null;
        ro?.observe(el);
        window.addEventListener('resize', schedule);

        // One frame later the container has its final size: fit, then hand over.
        const readyFrame = requestAnimationFrame(() => {
            refit();
            cb.current.onReady?.(term);
            if (autoFocus && !readOnly) term.focus();
        });

        return () => {
            cancelAnimationFrame(frame);
            cancelAnimationFrame(readyFrame);
            ro?.disconnect();
            window.removeEventListener('resize', schedule);
            dataSub.dispose();
            cb.current.onDispose?.();
            termRef.current = null;
            term.dispose();
        };
        // The options that shape the instance; changing one makes a new terminal.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [readOnly, fontSize, scrollback, webLinks, canvasRenderer, theme]);

    return (
        <div
            ref={containerRef}
            role="region"
            aria-label={ariaLabel}
            className={cn('min-h-0 overflow-hidden', className)}
            style={{ backgroundColor: theme.background, ...style }}
            onClick={() => { if (!readOnly) termRef.current?.focus(); }}
        />
    );
}
